import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PUBLIC_HINT = "Create a free account for details, pricing and booking.";

const AREAS = ["Clarity", "Confidence", "Direction", "Momentum", "Honesty"];

export default function Experiences() {
  const { user } = useAuth();
  const loc = useLocation();
  const [pkgs, setPkgs] = useState([]);
  const [full, setFull] = useState([]);
  useEffect(() => {
    api.get("/packages").then(r => setPkgs(r.data)).catch(() => setPkgs([]));
    if (user) api.get("/packages/full").then(r => setFull(r.data)).catch(() => setFull([]));
  }, [user]);

  const byKey = Object.fromEntries(full.map(p => [p.lookup_key, p]));

  return (
    <div data-testid="experiences-page" className="font-body">
      <section className="pt-32 pb-14 sm:pt-40 sm:pb-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="rc-eyebrow">Experiences</span>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-semibold mt-5" style={{ letterSpacing: "-0.03em", lineHeight: "1.05" }}>
            Three ways in. <span className="text-[#2d7cf6]">Pick yours.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[color:var(--rc-text-muted)] max-w-[55ch] leading-relaxed">
            Private 1:1 experiences, each shaped around you.
          </p>
          {!user && (
            <p data-testid="experiences-public-hint" className="mt-3 text-sm text-[color:var(--rc-text-muted)] max-w-[55ch] leading-relaxed">
              {PUBLIC_HINT}
            </p>
          )}
        </div>
      </section>

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      <section className="py-12 sm:py-16">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="rc-eyebrow">Areas of focus</span>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
            {AREAS.map((a) => (
              <span key={a} className="font-display text-xl sm:text-2xl font-semibold text-[color:var(--rc-text)]">{a}</span>
            ))}
          </div>
          <p className="mt-6 text-sm text-[color:var(--rc-text-muted)] max-w-xl leading-relaxed">
            Possible directions, not rigid categories. Every session moves at the pace of the client in front of me.
          </p>
        </div>
      </section>

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      <section className="py-16 sm:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {pkgs.map(p => {
              const detail = byKey[p.lookup_key]; // undefined for anonymous
              return (
                <div key={p.lookup_key} data-testid={`experience-${p.lookup_key}`} className={`rc-service-card ${p.signature ? "featured" : ""}`}>
                  <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#2d7cf6] mb-3">{p.subtitle}</span>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold mb-3">{p.name}</h3>
                  <p className="text-sm sm:text-base text-[color:var(--rc-text-muted)] leading-relaxed flex-1 mb-6">{p.tagline}</p>

                  {detail ? (
                    <>
                      <div className="font-display text-xl font-semibold mb-4">
                        ${(detail.amount / 100).toLocaleString()}{" "}
                        <span className="text-sm font-normal text-[color:var(--rc-text-muted)] font-body">/ {detail.duration}</span>
                      </div>
                      <ul className="list-none p-0 m-0 mb-6 space-y-2">
                        {detail.features?.map(f => (
                          <li key={f} className="text-sm text-[color:var(--rc-text-muted)] py-2 border-b border-[color:var(--rc-divider)] last:border-b-0 flex items-center gap-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#2d7cf6] flex-shrink-0" aria-hidden="true" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <Link to={`/dashboard/book?pkg=${p.lookup_key}`} state={{ pkg: p.lookup_key }} data-testid={`book-${p.lookup_key}`}
                            className={`rc-btn w-full ${p.signature ? "rc-btn-blue" : "rc-btn-outline-new"}`}>
                        Book {p.name} <ArrowRight className="w-4 h-4" />
                      </Link>
                    </>
                  ) : (
                    <div className="mt-auto">
                      <div data-testid={`gate-${p.lookup_key}`} className="flex items-center gap-2 text-xs text-[color:var(--rc-text-muted)] uppercase tracking-[0.12em] mb-4">
                        <Lock className="w-3 h-3" /> Members only
                      </div>
                      <Link
                        to={`/signup?pkg=${p.lookup_key}`}
                        state={{ from: `/dashboard/book?pkg=${p.lookup_key}` }}
                        data-testid={`join-${p.lookup_key}`}
                        className={`rc-btn w-full ${p.signature ? "rc-btn-blue" : "rc-btn-outline-new"}`}
                      >
                        Join the Rebel List <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/login?pkg=${p.lookup_key}`}
                        state={{ from: `/dashboard/book?pkg=${p.lookup_key}` }}
                        data-testid={`signin-${p.lookup_key}`}
                        className="mt-3 block text-center text-xs text-[color:var(--rc-text-muted)] hover:text-[color:var(--rc-text)] underline-editorial"
                      >
                        Already a member? Sign in
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <hr className="max-w-[1200px] mx-auto border-0 border-t border-[color:var(--rc-divider)]" />

      <section className="py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <span className="rc-eyebrow">Private · Personal · Unrushed</span>
          <h2 className="font-display text-3xl sm:text-5xl font-semibold mt-3" style={{ letterSpacing: "-0.02em" }}>
            Every Rebel experience is <span className="text-[#2d7cf6]">shaped around you.</span>
          </h2>
          <p className="mt-6 text-base text-[color:var(--rc-text)] max-w-[55ch] leading-relaxed" style={{ lineHeight: "1.65" }}>
            Create a free account to see what each experience includes and what it costs.
          </p>
          {!user && (
            <div className="mt-6">
              <Link
                to="/signup"
                state={{ from: loc.pathname }}
                data-testid="join-rebel-list-cta"
                className="rc-btn rc-btn-blue"
              >
                Join the Rebel List <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
